import { useCallback, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";

import { selectChatMessages, selectUserMessageCount } from "../../../store/chat/selectors";
import useLeadGeneratorChatQuery from "../queries/useLeadGeneratorChatQuery";
import { selectLeadGeneratorUserMessageData } from "../store";
import { setLeadGeneratorAgentMessage, setLeadGeneratorUserMessage } from "../store/effects";
import type { PocessedMessageData } from "../store/types";
import type { MdComponentProps } from "../types/mdComponentProps";
import type { ChatThreadModel } from "../types/models";

import { useLeadGeneratorChatInit } from "./useLeadGeneratorChatInit";
import { useMessageContentProcessor } from "./useMessageContentProcessor";

type HookParams = Pick<MdComponentProps<LeadsSearchAllFiltersTypes>, "filtersManager"> & {
    onMessageReceive: (messageData: PocessedMessageData | null) => void;
};

export const useLeadGeneratorChatMessages = ({ filtersManager, onMessageReceive }: HookParams): ChatThreadModel => {
    const dispatch = useDispatch();

    const messages = useSelector(selectChatMessages);
    const userMessageData = useSelector(selectLeadGeneratorUserMessageData);
    const userMessageCount = useSelector(selectUserMessageCount);

    const { threadId, isThreadCreationPending } = useLeadGeneratorChatInit();

    const {
        data,
        isLoading: isChatLoading,
        isPending: isChatPending,
        agentMessageId,
    } = useLeadGeneratorChatQuery({
        isEnabled: userMessageCount > 0,
        threadId,
        messageId: userMessageCount,
        messagePayload: userMessageData,
    });

    const isThreadPending = isThreadCreationPending || (isChatPending && messages.length === 0);

    const sendMessage = useCallback((message: string) => dispatch(setLeadGeneratorUserMessage({ message })), []);

    useEffect(() => {
        if (data && agentMessageId && !isChatLoading) {
            void dispatch(
                setLeadGeneratorAgentMessage({
                    content: data,
                    id: agentMessageId,
                }),
            ).then((messageData) => onMessageReceive(messageData));
        }
    }, [data, agentMessageId, isChatLoading]);

    useMessageContentProcessor({
        filtersManager,
        agentMessageId,
    });

    return {
        messages,
        isPending: isThreadPending,
        isLoading: isChatLoading,
        agentMessageId: agentMessageId,
        sendMessage,
    };
};
